/** Native disclosure navigation: one panel, outside/Escape closing, focus return. */
class HeaderNav extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.lanes = [...this.querySelectorAll('details[data-lane]')];
    this.querySelectorAll('[data-header-close]').forEach(button => { button.hidden = false; });
    this.lanes.forEach(lane => {
      lane.addEventListener('toggle', event => this.onToggle(event), options);
      lane.addEventListener('focusout', event => this.onFocusout(event), options);
    });
    document.addEventListener('keydown', event => this.onKeydown(event), options);
    this.addEventListener('click', event => {
      if (event.target.closest('[data-cart-open]')) this.closeAll();
      const close = event.target.closest('[data-header-close]');
      if (!close) return;
      const lane = close.closest('details[data-lane]');
      lane.open = false;
      lane.querySelector('summary').focus();
    }, options);
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.closeAll();
    }, options);
    // Tarjetas de Proyectos del panel móvil: el HTML las trae una vez (mega menú); se clonan al panel cuando hay tiempo libre o
    // al tocar el menú, antes de que se abra.
    this.cardsSlot = this.querySelector('[data-header-cards-slot]');
    if (this.cardsSlot) {
      const fill = () => this.fillCards();
      if ('requestIdleCallback' in window) requestIdleCallback(fill, { timeout: 3000 }); else setTimeout(fill, 1500);
      this.querySelector('.lane--shop > summary')?.addEventListener('pointerdown', fill, options);
      this.querySelector('.lane--shop > summary')?.addEventListener('click', fill, options);
    }
    this.updateLayout();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateLayout());
      this.resizeObserver.observe(this.querySelector('.site-header__main'));
    }
    this.mq = window.matchMedia('(min-width: 1100px)');
    this.onBreakpoint = () => this.closeAll();
    this.mq.addEventListener('change', this.onBreakpoint);
    if (this.classList.contains('site-header--sticky')) {
      window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.controller.signal });
      this.onScroll();
    }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.scrollFrame);
    this.classList.remove('site-header--compact');
    this.querySelectorAll('[data-header-close]').forEach(button => { button.hidden = true; });
    this.resizeObserver?.disconnect();
    this.controller?.abort();
    this.controller = null;
    this.mq?.removeEventListener('change', this.onBreakpoint);
  }

  fillCards() {
    const slot = this.cardsSlot;
    if (!slot || slot.dataset.filled) return;
    const source = this.querySelector('.lane--projects .menu-cards');
    if (!source) return;
    slot.dataset.filled = 'true';
    slot.replaceChildren(source.cloneNode(true));
  }

  updateLayout() {
    const main = this.querySelector('.site-header__main');
    const nav = this.querySelector('.site-header__nav');
    const navHeight = nav && getComputedStyle(nav).position === 'static' ? nav.getBoundingClientRect().height : 0;
    const height = this.classList.contains('site-header--sticky') ? main.getBoundingClientRect().height + navHeight : 0;
    document.documentElement.style.setProperty('--navigation-height', `${height}px`);
    this.style.setProperty('--header-bottom', `${Math.max(0, this.getBoundingClientRect().bottom)}px`);
  }

  /** Al scrollear, el header se compacta en una sola fila con el nombre al medio. */
  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      const compact = window.scrollY > 96;
      if (compact !== this.compact) {
        this.compact = compact;
        this.classList.toggle('site-header--compact', compact);
        this.updateLayout();
      }
    });
  }

  onToggle(event) {
    const lane = event.target;
    if (!lane.open) return;
    this.lanes.forEach(other => {
      if (other !== lane) other.open = false;
    });
    this.updateLayout();
  }

  onKeydown(event) {
    if (event.key !== 'Escape' || event.defaultPrevented || document.querySelector('dialog[open]')) return;
    const open = this.lanes.find(lane => lane.open);
    if (!open) return;
    open.open = false;
    open.querySelector('summary').focus();
  }

  onFocusout(event) {
    const lane = event.currentTarget;
    if (event.relatedTarget && !lane.contains(event.relatedTarget)) lane.open = false;
  }

  closeAll() {
    this.lanes.forEach(lane => { lane.open = false; });
  }
}

if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);
